import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import SUCCESS_MESSAGES from '../../../common/constants/SuccessMessagesConstants';
import { request } from '../../../common/util/RestClient';
import envConfig from '../../../config';
import {
  archivePlaybookInstance,
  completePlaybookInstance,
  fetchAllPlaybookInstances,
  fetchPlaybookInstanceById,
  markInProgressPlaybookInstance,
  updatePlaybookInstance,
  searchPlaybookInstances,
  fetchPlaybookInstanceChangelog,
  bulkCompletePlaybookInstance,
} from '../../routines/playbookInstanceRoutines';
import { fetchAllPlaybookTemplates } from '../../routines/playbookTemplateRoutines';

const PLAYBOOK_INSTANCE_URL = `${envConfig.APIG_URL}playbook/playbook-instances`;
const PM_URL = `${envConfig.APIG_URL}project-mgt/playbook-instances`;

export function* fetchAllPlaybookInstancesSaga() {
  try {
    const response = yield call(request, PLAYBOOK_INSTANCE_URL);
    yield put(fetchAllPlaybookInstances.success(response));
  } catch (error) {
    yield put(fetchAllPlaybookInstances.failure({ error }));
  } finally {
    yield put(fetchAllPlaybookInstances.fulfill());
  }
}

export function* fetchPlaybookInstanceByIdSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${payload.instanceId}`
    );
    yield put(fetchPlaybookInstanceById.success(response));
  } catch (error) {
    yield put(fetchPlaybookInstanceById.failure({ error }));
  } finally {
    yield put(fetchPlaybookInstanceById.fulfill());
  }
}

export function* fetchPlaybookInstanceChangeLogByIdSaga({ payload }) {
  const { associatedItemId: instanceId } = payload;
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${instanceId}/changelog`
    );
    yield put(fetchPlaybookInstanceChangelog.success(response));
  } catch (error) {
    yield put(fetchPlaybookInstanceChangelog.failure({ error }));
  } finally {
    yield put(fetchPlaybookInstanceChangelog.fulfill());
  }
}

export function* completePlaybookInstanceSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${payload.instanceId}/complete`,
      {
        method: 'POST',
        payload: {},
      }
    );
    yield put(
      completePlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.COMPLETE_PLAYBOOK_INSTANCE,
      })
    );
    yield put(fetchAllPlaybookInstances.trigger());
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(completePlaybookInstance.failure({ error }));
  } finally {
    yield put(completePlaybookInstance.fulfill());
  }
}

export function* bulkCompletePlaybookInstanceSaga({ payload }) {
  try {
    const { onSuccess, ..._payload } = payload;
    const response = yield call(request, `${PM_URL}/complete`, {
      method: 'POST',
      payload: _payload,
    });
    yield put(
      bulkCompletePlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.COMPLETE_PLAYBOOK_INSTANCE,
      })
    );
    if (onSuccess) onSuccess();
    yield put(fetchAllPlaybookInstances.trigger());
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(bulkCompletePlaybookInstance.failure({ error }));
  } finally {
    yield put(bulkCompletePlaybookInstance.fulfill());
  }
}

export function* markInProgressPlaybookInstanceSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${payload.instanceId}/in-progress`,
      {
        method: 'POST',
        payload: {},
      }
    );
    yield put(
      markInProgressPlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.MARK_IN_PROGRESS_PLAYBOOK_INSTANCE,
      })
    );
    yield put(fetchAllPlaybookInstances.trigger());
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(markInProgressPlaybookInstance.failure({ error }));
  } finally {
    yield put(markInProgressPlaybookInstance.fulfill());
  }
}

export function* updatePlaybookInstanceSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${payload.instanceId}`,
      {
        method: 'PATCH',
        payload: payload.formData,
      }
    );

    yield put(
      updatePlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.UPDATE_PLAYBOOK_INSTANCE,
      })
    );
    yield put(fetchAllPlaybookInstances.trigger());
  } catch (error) {
    yield put(updatePlaybookInstance.failure({ error }));
  } finally {
    yield put(updatePlaybookInstance.fulfill());
  }
}

export function* archivePlaybookInstanceSaga({ payload }) {
  try {
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/${payload.instanceId}/archive`,
      {
        method: 'POST',
        payload: {},
      }
    );
    yield put(
      archivePlaybookInstance.success({
        response,
        successMessage: SUCCESS_MESSAGES.ARCHIVE_PLAYBOOK_INSTANCE,
      })
    );
    yield put(fetchAllPlaybookInstances.trigger());
    yield put(fetchAllPlaybookTemplates.trigger());
  } catch (error) {
    yield put(archivePlaybookInstance.failure({ error }));
  } finally {
    yield put(archivePlaybookInstance.fulfill());
  }
}

export function* fetchActivePlaybookInstancesSaga({ payload }) {
  try {
    const { level, entityId } = payload;
    const response = yield call(
      request,
      `${PLAYBOOK_INSTANCE_URL}/search?orderBy=playbookInstanceId&order=desc&level=${level}&parentIdentifier=${entityId}`
    );
    yield put(searchPlaybookInstances.success(response));
  } catch (error) {
    yield put(searchPlaybookInstances.failure({ error }));
  } finally {
    yield put(searchPlaybookInstances.fulfill());
  }
}

export default function* root() {
  yield all([
    takeLatest(
      fetchAllPlaybookInstances.TRIGGER,
      fetchAllPlaybookInstancesSaga
    ),
    takeLatest(
      fetchPlaybookInstanceById.TRIGGER,
      fetchPlaybookInstanceByIdSaga
    ),
    takeLatest(
      fetchPlaybookInstanceChangelog.TRIGGER,
      fetchPlaybookInstanceChangeLogByIdSaga
    ),
    takeEvery(completePlaybookInstance.TRIGGER, completePlaybookInstanceSaga),
    takeEvery(
      bulkCompletePlaybookInstance.TRIGGER,
      bulkCompletePlaybookInstanceSaga
    ),
    takeEvery(
      markInProgressPlaybookInstance.TRIGGER,
      markInProgressPlaybookInstanceSaga
    ),
    takeEvery(archivePlaybookInstance.TRIGGER, archivePlaybookInstanceSaga),
    takeEvery(updatePlaybookInstance.TRIGGER, updatePlaybookInstanceSaga),
    takeEvery(
      searchPlaybookInstances.TRIGGER,
      fetchActivePlaybookInstancesSaga
    ),
  ]);
}
